import React, { useState, useEffect } from "react"
import FormLabel from "@mui/material/FormLabel"
import OutlinedInput from "@mui/material/OutlinedInput"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import { styled } from "@mui/system"
import Grid from "@mui/material/Grid2"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Button from "@mui/material/Button"
import Skeleton from "@mui/material/Skeleton"
import Alert from "@mui/material/Alert"
import CircularProgress from "@mui/material/CircularProgress"
import Box from "@mui/material/Box"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const titles = ["M.", "Mme.", "Mlle."]

const emptyPerson = {
    title: "",
    first_name: "",
    last_name: "",
    email: "",
    address_1: "",
    address_2: "",
    city: "",
    zip: "",
    country: "",
}

// ---------------------------------------------------------------------------
// Error message helper
//
// Rationale: raw Postgres / PostgREST messages are technical and in English.
// We translate the most common constraint violations into plain French so that
// non-developer users understand what went wrong and what to do.
// ---------------------------------------------------------------------------

const POSTGRES_ERROR_MESSAGES = {
    "23503": "Impossible de supprimer cette personne : elle est référencée par d'autres données de l'application.",
    "23505": "Ces informations correspondent à une personne déjà enregistrée (doublon détecté).",
    "23502": "Un champ obligatoire est manquant. Veuillez vérifier votre saisie.",
    "42501": "Vous n'avez pas les droits nécessaires pour effectuer cette action.",
    PGRST116: "La personne demandée est introuvable.",
}

function formatSupabaseError(error, operation) {
    const mapped = POSTGRES_ERROR_MESSAGES[error.code] ?? POSTGRES_ERROR_MESSAGES[error.hint]
    if (mapped) return mapped

    const opLabel =
        operation === "delete"
            ? "la suppression"
            : operation === "update"
                ? "la mise à jour"
                : "l'ajout"

    return `Erreur lors de ${opLabel} : ${error.message}`
}

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const FormGrid = styled(Grid)(() => ({
    display: "flex",
    flexDirection: "column",
}))

// ---------------------------------------------------------------------------
// Skeleton — mirrors the real form layout so there is no jarring reflow.
//
// Rationale: rendering a skeleton whose shape matches the real content
// prevents cumulative layout shift and signals to the user that data is on
// its way, not missing.
// ---------------------------------------------------------------------------

function FormSkeleton() {
    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>
                    {/* Title (civility) */}
                    <FormGrid size={{ xs: 12, xl: 4 }}>
                        <Skeleton variant="text" width="55%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* First name */}
                    <FormGrid size={{ xs: 12, md: 6, xl: 4 }}>
                        <Skeleton variant="text" width="40%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* Last name */}
                    <FormGrid size={{ xs: 12, md: 6, xl: 4 }}>
                        <Skeleton variant="text" width="40%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* Email */}
                    <FormGrid size={{ xs: 12 }}>
                        <Skeleton variant="text" width="25%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* Address 1 */}
                    <FormGrid size={{ xs: 12 }}>
                        <Skeleton variant="text" width="30%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* Address 2 */}
                    <FormGrid size={{ xs: 12 }}>
                        <Skeleton variant="text" width="45%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* ZIP */}
                    <FormGrid size={{ xs: 12, sm: 5, xl: 4 }}>
                        <Skeleton variant="text" width="50%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* City */}
                    <FormGrid size={{ xs: 12, sm: 7, xl: 8 }}>
                        <Skeleton variant="text" width="25%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* Country */}
                    <FormGrid size={{ xs: 12 }}>
                        <Skeleton variant="text" width="20%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    {/* Buttons */}
                    <FormGrid size={{ xs: 6 }} sx={{ mt: 1 }}>
                        <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                    <FormGrid size={{ xs: 6 }} sx={{ mt: 1 }}>
                        <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                    </FormGrid>
                </Grid>
            </CardContent>
        </Card>
    )
}

// ---------------------------------------------------------------------------
// PeopleForm
//
// Responsibilities:
//   - Fetch the selected person from Supabase when selectedPersonId changes.
//   - Show a skeleton while the row is being loaded.
//   - Handle INSERT, UPDATE, DELETE with proper error reporting.
//   - Call onSuccess() after a successful mutation so the parent can clear
//     the selection.
//
// Data decisions:
//   - We fetch directly from Supabase rather than relying on the table's
//     in-memory snapshot.  This guarantees the form always edits the freshest
//     version of the row, even if another user modified it between the
//     selection and the edit.
//   - Mutations are fire-and-forget from the form's perspective: the table
//     subscribes to realtime events and will update itself independently.
// ---------------------------------------------------------------------------

export default function PeopleForm({ supabase, selectedPersonId, onSuccess }) {
    const [person, setPerson] = useState(emptyPerson)
    const [fetchLoading, setFetchLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)

    // Fetch the selected person whenever the ID changes.
    useEffect(() => {
        if (!selectedPersonId) {
            setPerson(emptyPerson)
            setError(null)
            return
        }

        let cancelled = false // Guard against race conditions if the user
                              // clicks through rows quickly.

        const fetchPerson = async () => {
            setFetchLoading(true)
            setError(null)

            const { data, error: fetchError } = await supabase
                .from("person")
                .select("*")
                .eq("id", selectedPersonId)
                .single()

            if (cancelled) return

            if (fetchError) {
                setError(formatSupabaseError(fetchError, "fetch"))
                setFetchLoading(false)
                return
            }

            setPerson(data)
            setFetchLoading(false)
        }

        fetchPerson()

        return () => {
            cancelled = true
        }
    }, [selectedPersonId, supabase])

    // -------------------------------------------------------------------------
    // Input handlers
    // -------------------------------------------------------------------------

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setPerson((prev) => ({ ...prev, [name]: value }))
    }

    // Strip empty / null / undefined fields before sending to the API.
    const cleanPayload = (raw) =>
        Object.fromEntries(
            Object.entries(raw).filter(
                ([_, v]) =>
                    v !== "" &&
                    v !== undefined &&
                    v !== null &&
                    v !== "null" &&
                    v !== "undefined"
            )
        )

    // -------------------------------------------------------------------------
    // Mutations
    // -------------------------------------------------------------------------

    const handleSubmit = async () => {
        setSubmitting(true)
        setError(null)

        const payload = cleanPayload(person)

        let supabaseError

        if (selectedPersonId) {
            // Strip server-managed columns before updating.
            const { id, created_at, updated_at, ...updatePayload } = payload
            const { error } = await supabase
                .from("person")
                .update(updatePayload)
                .eq("id", selectedPersonId)
            supabaseError = error
        } else {
            const { error } = await supabase.from("person").insert(payload)
            supabaseError = error
        }

        setSubmitting(false)

        if (supabaseError) {
            setError(formatSupabaseError(supabaseError, selectedPersonId ? "update" : "insert"))
            return
        }

        // Success: reset and tell the parent to clear the selection.
        setPerson(emptyPerson)
        if (onSuccess) onSuccess()
    }

    const handleDelete = async () => {
        setSubmitting(true)
        setError(null)

        const { error: deleteError } = await supabase
            .from("person")
            .delete()
            .eq("id", selectedPersonId)

        setSubmitting(false)

        if (deleteError) {
            // 23503 is the most common: FK constraint prevents deletion.
            setError(formatSupabaseError(deleteError, "delete"))
            return
        }

        setPerson(emptyPerson)
        if (onSuccess) onSuccess()
    }

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    // Show skeleton while loading the selected person's data.
    // Rationale: we only skeleton when a row is selected (fetchLoading=true).
    // For the "add" mode (no selection) there is nothing to load, so we render
    // the empty form immediately.
    if (fetchLoading) return <FormSkeleton />

    return (
        <Card variant="outlined">
            <CardContent>
                {error && (
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        sx={{ mb: 2 }}
                    >
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <FormGrid size={{ xs: 12, xl: 4 }}>
                        <FormLabel htmlFor="title" required>
                            Titre de civilité
                        </FormLabel>
                        <Select
                            id="title"
                            name="title"
                            value={person.title}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        >
                            {titles.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormGrid>

                    <FormGrid size={{ xs: 12, md: 6, xl: 4 }}>
                        <FormLabel htmlFor="first-name" required>
                            Prénom
                        </FormLabel>
                        <OutlinedInput
                            id="first-name"
                            name="first_name"
                            placeholder="Jacques"
                            value={person.first_name}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12, md: 6, xl: 4 }}>
                        <FormLabel htmlFor="last-name" required>
                            Nom
                        </FormLabel>
                        <OutlinedInput
                            id="last-name"
                            name="last_name"
                            placeholder="Chirac"
                            value={person.last_name}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="email" required>
                            Email
                        </FormLabel>
                        <OutlinedInput
                            id="email"
                            name="email"
                            placeholder="jacques.chirac@gouv.fr"
                            value={person.email}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="address-1" required>
                            Adresse
                        </FormLabel>
                        <OutlinedInput
                            id="address-1"
                            name="address_1"
                            placeholder="55 Rue du Faubourg Saint-Honoré"
                            value={person.address_1}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="address-2" sx={{ mb: "5px" }}>
                            Complément d'adresse
                        </FormLabel>
                        <OutlinedInput
                            id="address-2"
                            name="address_2"
                            placeholder="Bureau #1"
                            value={person.address_2}
                            onChange={handleInputChange}
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12, sm: 5, xl: 4 }}>
                        <FormLabel htmlFor="zip" required>
                            Code postal
                        </FormLabel>
                        <OutlinedInput
                            id="zip"
                            name="zip"
                            placeholder="75008"
                            value={person.zip}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12, sm: 7, xl: 8 }}>
                        <FormLabel htmlFor="city" required>
                            Ville
                        </FormLabel>
                        <OutlinedInput
                            id="city"
                            name="city"
                            placeholder="Paris"
                            value={person.city}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="country" required>
                            Pays
                        </FormLabel>
                        <OutlinedInput
                            id="country"
                            name="country"
                            placeholder="France"
                            value={person.country}
                            onChange={handleInputChange}
                            required
                            size="small"
                            disabled={submitting}
                        />
                    </FormGrid>

                    <FormGrid
                        size={{ xs: selectedPersonId ? 6 : 12 }}
                        sx={{ mt: "10px" }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting}
                            startIcon={
                                submitting && !selectedPersonId ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : null
                            }
                        >
                            Valider
                        </Button>
                    </FormGrid>

                    {selectedPersonId && (
                        <FormGrid size={{ xs: 6 }} sx={{ mt: "10px" }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={submitting}
                                startIcon={
                                    submitting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : null
                                }
                            >
                                Supprimer
                            </Button>
                        </FormGrid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}